import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import dayjs from 'dayjs';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { WeighbridgeFilters, PaginationMeta } from '../types';
import { weighbridgeService } from '../services/weighbridgeService';
import { DEFAULT_COLUMN_KEYS } from '../components/columnsConfig';

export const useWeighbridgeReport = () => {
    const queryClient = useQueryClient();
    const [isExporting, setIsExporting] = useState<boolean>(false);

    // Menyimpan total records yang sudah diketahui untuk filter aktif
    const totalRecordsRef = useRef<number | undefined>(undefined);

    // Default filters: hari ini (today) dan company_id = 1 (GBS)
    const [filters, setFilters] = useState<WeighbridgeFilters>({
        date_from: dayjs().format('YYYY-MM-DD'),
        date_to: dayjs().format('YYYY-MM-DD'),
        company_id: 1,
        estate_ids: [],
        page: 1,
        per_page: 20,
    });

    const [visibleColumns, setVisibleColumns] = useState<string[]>(DEFAULT_COLUMN_KEYS);

    const filterBaseKey = useMemo(() => [
        filters.date_from,
        filters.date_to,
        filters.company_id,
        (filters.estate_ids || []).slice().sort().join(','),
        filters.per_page,
    ], [filters.date_from, filters.date_to, filters.company_id, filters.estate_ids, filters.per_page]);

    // Query key konsisten HANYA berdasarkan filter dan nomor page
    const queryKey = useMemo(() => [
        'weighbridge_report',
        ...filterBaseKey,
        filters.page,
    ], [filterBaseKey, filters.page]);

    const {
        data,
        isLoading,
        isFetching,
        isPlaceholderData,
        error: queryError,
    } = useQuery({
        queryKey,
        queryFn: () => {
            const requestFilters: WeighbridgeFilters = {
                ...filters,
                known_total_records: filters.page && filters.page > 1 ? totalRecordsRef.current : undefined,
            };
            return weighbridgeService.getReport(requestFilters);
        },
        placeholderData: keepPreviousData,
        staleTime: 10 * 60 * 1000, // 10 menit tetap fresh di cache
        gcTime: 30 * 60 * 1000,    // 30 menit disimpan di memori browser
        refetchOnWindowFocus: false,
    });

    // Simpan total records ke ref begitu didapat dari server
    if (data?.total_records && data.total_records > 0) {
        totalRecordsRef.current = data.total_records;
    }

    const records = data?.data || [];
    const pagination: PaginationMeta = {
        page: data?.page || filters.page || 1,
        per_page: data?.per_page || filters.per_page || 20,
        total_records: data?.total_records || totalRecordsRef.current || 0,
        total_pages: data?.total_pages || 1,
    };

    // Prefetch page berikutnya secara cerdas
    useEffect(() => {
        if (!data) return;

        const totalRecords = data.total_records || totalRecordsRef.current;
        const currentPage = data.page;
        const totalPages = data.total_pages;

        if (currentPage < totalPages) {
            const nextPage = currentPage + 1;
            const nextQueryKey = ['weighbridge_report', ...filterBaseKey, nextPage];

            queryClient.prefetchQuery({
                queryKey: nextQueryKey,
                queryFn: () =>
                    weighbridgeService.getReport({
                        ...filters,
                        page: nextPage,
                        known_total_records: totalRecords,
                    }),
                staleTime: 10 * 60 * 1000,
            });
        }
    }, [data, filters, filterBaseKey, queryClient]);

    const error = queryError
        ? (queryError as any).response?.data?.detail || queryError.message || 'Gagal memuat data timbangan'
        : null;

    const fetchData = useCallback((customFilters?: Partial<WeighbridgeFilters>) => {
        totalRecordsRef.current = undefined;
        setFilters((prev) => ({
            ...prev,
            ...customFilters,
            page: 1, // Reset ke page 1 jika ganti filter
        }));
    }, []);

    const changePage = useCallback((newPage: number) => {
        setFilters((prev) => ({
            ...prev,
            page: newPage,
        }));
    }, []);

    const changePerPage = useCallback((newPerPage: number) => {
        totalRecordsRef.current = undefined;
        setFilters((prev) => ({
            ...prev,
            page: 1,
            per_page: newPerPage,
        }));
    }, []);

    const exportExcel = useCallback(async () => {
        setIsExporting(true);
        try {
            await weighbridgeService.downloadFullExcel(
                filters,
                `Laporan_Timbang_${filters.date_from}_sd_${filters.date_to}`,
                visibleColumns
            );
        } finally {
            setIsExporting(false);
        }
    }, [filters, visibleColumns]);

    // Loading spinner presisi:
    // 1. isLoading: Saat initial mount query belum memiliki data apapun
    // 2. isFetching && isPlaceholderData: Saat user ganti filter (tanggal, estate, company, baris per page)
    //    atau buka page baru yang belum ada di cache, TanStack Query meminjam data lama sebagai placeholder selagi data baru ditarik.
    // 3. Cache HIT: Ketika membuka page atau filter yang sudah dicache (atau prefetch selesai), isPlaceholderData === false,
    //    sehingga showLoading langsung false (instan 0 ms tanpa kedip spinner).
    const showLoading = isLoading || (isFetching && isPlaceholderData);

    return {
        records,
        pagination,
        loading: showLoading,
        isExporting,
        error,
        filters,
        visibleColumns,
        setVisibleColumns,
        fetchData,
        changePage,
        changePerPage,
        exportExcel,
    };
};
